import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire-side decimal: server returns Prisma Decimal as a canonical string; allow
// the same union the rest of the schema layer uses (see ar.ts / project.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed counterpart — for net/derived figures that can go negative (a CIP closing
// balance, or withheld−released retention on a data anomaly). Same inferred type.
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
export const CipCostPoolSchema = z.enum([
    'DIRECT_EQ',
    'DIRECT_CONS',
    'INDIRECT',
    'BORROW',
    'OTHER',
]);
// ============================================================
// CipProjectStatus — mirrors Prisma enum CipProjectStatus
// ============================================================
// SUSPENDED (Phase 5, IAS 23.20) — active development paused; the TFRS 23 engine
// excludes SUSPENDED projects from a period's borrowing-cost allocation.
export const CipProjectStatusSchema = z.enum([
    'ACTIVE',
    'SUSPENDED',
    'READY',
    'CAPITALIZED',
    'CANCELLED',
]);
// ============================================================
// CipBorrowingPolicy — mirrors Prisma enum CipBorrowingPolicy. TFRS 23 opt-in:
//   NONE         — project does not capitalize borrowing cost
//   SPECIFIC     — interest from a single tied loan (Loan.id), capped at the
//                  loan's actual interest
//   GENERAL_POOL — weighted cap-rate of all general-purpose loans, with the
//                  IAS 23 pool-wide actual-interest cap
// ============================================================
export const CipBorrowingPolicySchema = z.enum(['NONE', 'SPECIFIC', 'GENERAL_POOL']);
// ============================================================
// CipProject — header row
// ============================================================
export const CipProjectSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1), // CIP-YYYY-NNNNNN
    projectId: z.uuid().nullable().optional(),
    code: z.string().min(1).max(64),
    name: z.string().min(1),
    pm: z.string().nullable().optional(),
    budget: DecimalLike,
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    startDate: z.coerce.date(),
    expectedCompletionDate: z.coerce.date().nullable().optional(),
    status: CipProjectStatusSchema,
    accumulatedCost: DecimalLike,
    capitalizedAt: z.coerce.date().nullable().optional(),
    readyForUseDate: z.coerce.date().nullable().optional(),
    // Phase 5 (IAS 23.20) — set when status flips to SUSPENDED; cleared on resume.
    suspendedAt: z.coerce.date().nullable().optional(),
    suspendReason: z.string().nullable().optional(),
    borrowingPolicy: CipBorrowingPolicySchema,
    tiedLoanId: z.uuid().nullable().optional(),
    fixedAssetId: z.uuid().nullable().optional(),
    postedBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Pool-level cost totals computed by the server for each project row.
export const CipPoolTotalsSchema = z.object({
    DIRECT_EQ: DecimalLike,
    DIRECT_CONS: DecimalLike,
    INDIRECT: DecimalLike,
    BORROW: DecimalLike,
    OTHER: DecimalLike,
});
// List-row shape: adds aggregate borrowingCost + entryCount + pool totals.
export const CipProjectListRowSchema = CipProjectSchema.extend({
    borrowingCost: DecimalLike,
    entryCount: z.number().int().nonnegative(),
    branchName: z.string().nullable().optional(),
    poolTotals: CipPoolTotalsSchema,
});
// ============================================================
// CipCostEntry — child rows
// ============================================================
export const CipCostEntrySchema = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    entryDate: z.coerce.date(),
    description: z.string().min(1),
    accountCode: z.string().min(1),
    amount: DecimalLike,
    pool: CipCostPoolSchema,
    isCapitalizable: z.boolean(),
    vendorName: z.string().nullable().optional(),
    sourceRef: z.string().nullable().optional(),
    // Phase 4 — contractor progress-bill withholdings (off-CIP; only `amount` is
    // capitalized). vatAmount = input VAT, whtAmount = ภาษีหัก ณ ที่จ่าย,
    // retentionAmount = เงินประกันผลงาน.
    vatAmount: DecimalLike.nullable().optional(),
    whtAmount: DecimalLike.nullable().optional(),
    retentionAmount: DecimalLike.nullable().optional(),
    isBorrowingCost: z.boolean(),
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string(),
    createdAt: z.coerce.date(),
});
// ============================================================
// Wire — POST /cip-projects
// ============================================================
export const CreateCipProjectSchema = z.object({
    // Optional link to existing master Project (UUID). When omitted, the CIP
    // project is standalone.
    projectId: z.uuid().nullable().optional(),
    code: z.string().min(1).max(64),
    name: z.string().min(1),
    pm: z.string().nullable().optional(),
    budget: z.coerce.number().nonnegative().default(0),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    startDate: z.iso.date(),
    expectedCompletionDate: z.iso.date().nullable().optional(),
    // TFRS 23 borrowing-cost opt-in. tiedLoanId → Loan.id (NOT master Project);
    // required by the service when borrowingPolicy = SPECIFIC.
    borrowingPolicy: CipBorrowingPolicySchema.default('NONE'),
    tiedLoanId: z.uuid().nullable().optional(),
});
// ============================================================
// Wire — POST /cip-projects/:id/cost-entries
// Auto-JV: Dr 1240 CIP / Cr {sourceCashAccountCode | 2110 AP}
// ============================================================
export const CreateCipCostEntrySchema = z.object({
    entryDate: z.iso.date(),
    description: z.string().min(1),
    // The 5xxx-style cost code is recorded on the entry for audit (it would have
    // been the Dr account if the cost weren't being capitalized). The JV itself
    // uses 1240 on the Dr side regardless — see service for details.
    accountCode: z.string().min(1),
    amount: z.coerce.number().positive(),
    pool: CipCostPoolSchema,
    isCapitalizable: z.boolean().default(true),
    vendorName: z.string().nullable().optional(),
    sourceRef: z.string().nullable().optional(),
    // When omitted defaults to 2110 (AP). For petty-cash flows pass 1141, for
    // bank pass the relevant 11xx account.
    sourceCashAccountCode: z.string().min(1).optional(),
    // Phase 4 — contractor progress-bill withholdings. The JV becomes multi-line:
    //   Dr CIP_ASSET amount [+ Dr VAT_INPUT vatAmount]
    //     Cr WHT_PAYABLE whtAmount / Cr RETENTION_PAYABLE retentionAmount
    //     Cr source (= amount + vatAmount − whtAmount − retentionAmount)
    // Only `amount` is capitalized; vat/wht/retention never touch CIP.
    vatAmount: z.coerce.number().nonnegative().optional(),
    whtAmount: z.coerce.number().nonnegative().optional(),
    retentionAmount: z.coerce.number().nonnegative().optional(),
    isBorrowingCost: z.boolean().default(false),
});
// ============================================================
// Wire — POST /cip-projects/:id/capitalize
// Dr 1210 PP&E / Cr 1240 CIP  (amount = accumulatedCost)
//
// In addition to the transfer JV, the server creates a FixedAsset master row
// (no acquisition JV — CIP already posted Dr 1210). Optional FA attributes:
//   categoryCode     — AssetCategory code (defaults to 'BUILDING' in service)
//   usefulLifeMonths — overrides the 240-month (20-yr building) default
//   branchId         — branch the asset is registered against
// ============================================================
// ============================================================
// Capitalization split — one line in a multi-asset capitalization. Each split
// spawns its own FixedAsset (per-split category/useful-life). The server
// validates Σ(amount) === accumulatedCost before posting.
// ============================================================
export const CipCapitalizeSplitSchema = z.object({
    name: z.string().min(1),
    categoryCode: z.string().min(1),
    usefulLifeMonths: z.number().int().positive(),
    amount: z.coerce.number().positive(),
    salvageValue: z.coerce.number().nonnegative().optional(),
});
export const CapitalizeCipProjectSchema = z.object({
    capitalDate: z.iso.date(),
    categoryCode: z.string().min(1).optional(),
    usefulLifeMonths: z.number().int().positive().optional(),
    branchId: z.uuid().optional(),
    splits: z.array(CipCapitalizeSplitSchema).optional(),
});
// ============================================================
// Wire — POST /cip-projects/:id/mark-ready  (ACTIVE → READY)
// ============================================================
export const MarkReadyCipSchema = z.object({
    readyForUseDate: z.iso.date(),
});
// ============================================================
// Phase 5 (IAS 23.20) — suspend / resume capitalization.
//   POST :id/suspend  ACTIVE → SUSPENDED (stamps suspendedAt + suspendReason)
//   POST :id/resume   SUSPENDED → ACTIVE (clears suspendedAt + suspendReason)
// During SUSPENDED the TFRS 23 engine produces no borrowing line for the project
// (period-granularity: a project suspended mid-period is excluded for the whole
// period). reason is required (audit trail for the suspension).
// ============================================================
export const SuspendCipProjectSchema = z.object({
    suspendDate: z.iso.date(),
    reason: z.string().min(1),
});
export const ResumeCipProjectSchema = z.object({
    resumeDate: z.iso.date(),
});
// ============================================================
// Wire — POST /cip-projects/:id/write-off  (abandoned CIP)
// Dr {writeOffAccountCode | MISC_EXPENSE} / Cr 1240 CIP → status CANCELLED
// ============================================================
export const WriteOffCipProjectSchema = z.object({
    writeOffDate: z.iso.date(),
    reason: z.string().min(1),
    writeOffAccountCode: z.string().min(1).optional(),
});
// ============================================================
// Phase 4 — contractor retention release (เงินประกันผลงาน).
// Wire — POST /cip-projects/:id/retention/release
//   Dr RETENTION_PAYABLE amount / Cr {sourceCashAccountCode | source} amount
// (no CIP impact). amount must be ≤ project outstanding retention.
// ============================================================
export const CreateCipRetentionReleaseSchema = z.object({
    vendorName: z.string().nullable().optional(),
    amount: z.coerce.number().positive(),
    releaseDate: z.iso.date(),
    // When omitted the service defaults the Cr side to AP_TRADE (or a bank code).
    sourceCashAccountCode: z.string().min(1).optional(),
});
// GET /cip-projects/:id/retention → outstanding retention summary for a project.
//   outstanding = withheld − released
export const CipRetentionSummarySchema = z.object({
    projectId: z.uuid(),
    withheld: DecimalLike,
    released: DecimalLike,
    outstanding: SignedDecimalLike,
});
// ============================================================
// CIP rollforward — month-end report row
//   closing = opening + additions - capitalizedOut - writtenOff
// ============================================================
export const CipRollforwardRowSchema = z.object({
    id: z.uuid(),
    voucherNo: z.string(),
    name: z.string(),
    branchName: z.string().nullable().optional(),
    opening: DecimalLike,
    additions: DecimalLike,
    capitalizedOut: DecimalLike,
    writtenOff: DecimalLike,
    closing: SignedDecimalLike,
});
// ============================================================
// TFRS 23 borrowing-cost engine (Phase 3a) — wire DTOs.
// ============================================================
// One per-project allocation row in the preview / run result.
export const CipBorrowingPreviewRowSchema = z.object({
    cipProjectId: z.uuid(),
    voucherNo: z.string(),
    name: z.string(),
    regime: CipBorrowingPolicySchema,
    loanRef: z.string(),
    avgCipBase: DecimalLike,
    rateApplied: DecimalLike,
    rawInterest: DecimalLike,
    capitalizable: DecimalLike,
});
// computeCapRate result: the weighted general-loan rate + the loans that fed it.
export const CipCapRateResultSchema = z.object({
    period: z.string(),
    capRate: DecimalLike,
    generalLoans: z.array(z.object({
        code: z.string(),
        bank: z.string(),
        principal: DecimalLike,
        rate: DecimalLike,
    })),
});
// Body for POST borrowing/preview + borrowing/run. period = 'YYYYMM'.
export const CipBorrowingPeriodSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
// ============================================================
// Phase 5 (IAS 23.26) — the two required disclosures for an accounting year:
//   (a) totalCapitalized — total borrowing cost capitalized in the period, and
//   (b) capRates — the capitalization rate used per posted run (period @ rate).
// Sourced from CipBorrowingRun rows whose period starts with `year` (YYYY).
// totalCapitalized = Σ run.totalAmount; each capRates entry mirrors one run.
// ============================================================
export const CipBorrowingDisclosureSchema = z.object({
    year: z.string(),
    totalCapitalized: DecimalLike,
    capRates: z.array(z.object({ period: z.string(), capRate: DecimalLike, amount: DecimalLike })),
});
// ============================================================
// Phase 3b — approval-gating wire DTOs (shared by the borrowing-run [docType CB]
// and capitalize-request [docType CI] engine flows). Identical shape to the
// payment-request decision DTOs — the central engine is doc-type agnostic.
//   approve  → { step, comment? }
//   reject   → { step, reason }
//   return   → { step, reason } (resubmittable)
// ============================================================
export const CipApprovalDecisionSchema = z.object({
    step: z.coerce.number().int().positive(),
    comment: z.string().optional(),
    // M7-P3: audited SoD-override justification, threaded to the engine.
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const CipApprovalRejectSchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1).max(500),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
// Engine-state string unions for the two new approval-gated documents. The
// accounting `status` walks DRAFT → POSTED/CAPITALIZED on final approve, or
// CANCELLED on reject; `approvalStatus` walks the neutral engine machine.
export const CipBorrowingRunStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'POSTED',
    'CANCELLED',
    'RETURNED',
]);
export const CipCapitalizeRequestStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'CAPITALIZED',
    'CANCELLED',
    'RETURNED',
]);
//# sourceMappingURL=cip.js.map